import { Calendar } from 'lucide-react';
import { type ChangeEvent, useRef } from 'react';

// Form primitives shared by phone-preview screens (designer:
// sc-workflow-preview__field / __input / __hint-row). When `value`/`onChange`
// are provided the field is live (session runtime); otherwise it renders as
// the builder's presentational mock.

export function Field({
  label,
  placeholder,
  optional,
  hint,
  value,
  onChange,
  error,
  inputMode,
}: {
  label: string;
  placeholder: string;
  optional?: boolean;
  hint?: string;
  value?: string;
  onChange?: (value: string) => void;
  /** Validation message (session runtime). When set, the input turns red and
   *  the message replaces the hint. */
  error?: string;
  /**
   * Which on-screen keyboard a phone should open.
   *
   * The FIELD'S VALIDATOR decides this — never the placeholder, which often looks
   * numeric on a field that accepts letters. Pass `numeric` only when the validator
   * rejects letters outright:
   *
   *   numeric  Aadhaar `^\d{12}$` · bank account `^\d{9,18}$` · LPG id `^\d{17}$`
   *            · mobile `^[6-9]\d{9}$` · dates `^\d{2}[/-]\d{2}[/-]\d{4}$`
   *   omit     PAN, GSTIN, IFSC, EPIC `^[A-Z]{3}[0-9]{7}$`, passport
   *            `^[A-Z][0-9]{7}$`, Udyam, driving licence, Consumer ID
   *            `^[a-zA-Z0-9]+$`, Ration Card Number (no format check at all)
   *
   * Getting this wrong in the permissive direction only costs a tap; getting it wrong
   * the other way makes the field IMPOSSIBLE to fill on a phone, because a numeric
   * keypad has no letters. Consumer ID and Ration Card Number look like numbers and
   * are not — check `*.utils.ts` before adding one.
   */
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
}) {
  return (
    <div className="mb-1.5 flex flex-col gap-1">
      <span
        className="block text-[14px] font-medium"
        style={{ color: 'var(--brand-input-label, #000000)' }}
      >
        {label}
        {optional ? (
          <span className="text-[#B3B3B3]"> (Optional)</span>
        ) : (
          <span className="text-red-500">*</span>
        )}
      </span>
      <div className="relative">
        <input
          placeholder={placeholder}
          inputMode={inputMode}
          value={value}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          aria-invalid={error ? true : undefined}
          style={
            error
              ? undefined
              : {
                  backgroundColor: 'var(--brand-input-fill, #ffffff)',
                  borderColor: 'var(--brand-input-stroke, #D1D5DB)',
                  borderRadius: 'var(--brand-input-radius, 0.5rem)',
                }
          }
          className={`w-full border px-3.5 py-2.5 text-[13px] text-[#111827] outline-none placeholder:text-[#A5A5A5] ${
            error ? 'rounded-lg border-red-500' : ''
          }`}
        />
      </div>
      {error ? (
        <div className="mt-0.5">
          <span className="text-[length:var(--text-10)] text-red-500">{error}</span>
        </div>
      ) : (
        hint && (
          <div className="mt-0.5 flex items-center justify-end">
            <span className="text-right text-[length:var(--text-12)] text-[#666666]">{hint}</span>
          </div>
        )
      )}
    </div>
  );
}

// Date-of-Birth field. The value is a DD/MM/YYYY display string. The applicant
// can EITHER type it directly in the text box OR click the calendar icon to pick
// it (a native date input sits over the icon and fills DD/MM/YYYY on select).
// Auto-formats free-typed digits into DD/MM/YYYY as the applicant types
// (e.g. "13032002" → "13/03/2002"); backspacing re-derives cleanly.
function maskDob(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length > 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

// DD/MM/YYYY (or DD-MM-YYYY) → yyyy-MM-dd for the native <input type="date"> value,
// so the picker opens on the already-entered date. Empty when not a full valid date.
function toIsoDate(v?: string): string {
  const m = v?.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}

// Generic DD/MM/YYYY date field with the same UX as the PAN DobField: the
// applicant can type digits (auto-masked to DD/MM/YYYY) OR click the calendar
// icon to pick a date (a native date input overlays the icon). Used by any
// screen that needs a labelled date input (Driving Licence DOB, Passport
// DOB / Date of Issue, …).
export function DateField({
  label,
  value,
  onChange,
  hint,
  error,
  required,
  placeholder = 'DD/MM/YYYY',
}: {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  hint?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
}) {
  const dateRef = useRef<HTMLInputElement>(null);

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    if (!onChange) return;
    const iso = e.currentTarget.value; // yyyy-mm-dd from the native picker
    if (!iso) {
      onChange('');
      return;
    }
    const [year, month, day] = iso.split('-');
    onChange(`${day}/${month}/${year}`);
  }

  // Programmatically open the native calendar from the icon button — reliable
  // across the whole icon, not just a tiny transparent hit-strip. showPicker() is
  // supported on modern Chromium; fall back to focus/click on older engines.
  function openPicker() {
    const el = dateRef.current;
    if (!el) return;
    try {
      el.showPicker();
    } catch {
      el.focus();
      el.click();
    }
  }

  return (
    <div className="mb-1.5 flex flex-col gap-1">
      <span
        className="block text-[14px] font-medium"
        style={{ color: 'var(--brand-input-label, #000000)' }}
      >
        {label}
        {required ? (
          <span className="text-red-500">*</span>
        ) : (
          <span className="text-[#B3B3B3]"> (Optional)</span>
        )}
      </span>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          value={value}
          onChange={onChange ? (e) => onChange(maskDob(e.target.value)) : undefined}
          maxLength={10}
          aria-invalid={error ? true : undefined}
          style={
            error
              ? undefined
              : {
                  backgroundColor: 'var(--brand-input-fill, #ffffff)',
                  borderColor: 'var(--brand-input-stroke, #D1D5DB)',
                  borderRadius: 'var(--brand-input-radius, 0.5rem)',
                }
          }
          className={`w-full border px-3.5 py-2.5 pr-10 text-[13px] text-[#111827] outline-none placeholder:text-[#A5A5A5] ${
            error ? 'rounded-lg border-red-500' : ''
          }`}
        />
        {/* Calendar button — opens the native picker; the text field stays typeable. */}
        <button
          type="button"
          aria-label={`Pick ${label.toLowerCase()}`}
          onClick={openPicker}
          className="absolute right-0 top-0 flex h-full w-10 items-center justify-center text-[#9CA3AF] hover:text-[#6B7280]"
        >
          <Calendar size={18} strokeWidth={2} />
        </button>
        {/* Hidden native date input — the picker anchor + value holder. Not clicked
            directly; opened via the button's showPicker(). */}
        <input
          ref={dateRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          value={toIsoDate(value)}
          onChange={onPick}
          className="pointer-events-none absolute right-2 bottom-0 h-0 w-0 opacity-0"
        />
      </div>
      {error ? (
        <div className="mt-0.5">
          <span className="text-[length:var(--text-10)] text-red-500">{error}</span>
        </div>
      ) : (
        hint && (
          <div className="mt-0.5 flex items-center justify-end">
            <span className="text-right text-[length:var(--text-12)] text-[#666666]">{hint}</span>
          </div>
        )
      )}
    </div>
  );
}

export function DobField({
  dob,
  setDob,
  required,
}: {
  dob: string;
  setDob: (v: string) => void;
  // When true the field is mandatory (Return-Data has date_of_birth/age enabled),
  // shown with a red asterisk instead of "(Optional)".
  required?: boolean;
}) {
  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const iso = e.currentTarget.value; // yyyy-mm-dd from the native picker
    if (!iso) {
      setDob('');
      return;
    }
    const [year, month, day] = iso.split('-');
    setDob(`${day}/${month}/${year}`);
  }

  return (
    <div className="mb-1.5 flex flex-col gap-1">
      <span
        className="block text-[14px] font-medium"
        style={{ color: 'var(--brand-input-label, #000000)' }}
      >
        Date of Birth
        {required ? (
          <span className="text-red-500">*</span>
        ) : (
          <span className="text-[#B3B3B3]"> (Optional)</span>
        )}
      </span>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          placeholder="DD/MM/YYYY"
          value={dob}
          onChange={(e) => setDob(maskDob(e.target.value))}
          maxLength={10}
          style={{
            backgroundColor: 'var(--brand-input-fill, #ffffff)',
            borderColor: 'var(--brand-input-stroke, #D1D5DB)',
            borderRadius: 'var(--brand-input-radius, 0.5rem)',
          }}
          className="w-full border px-3.5 py-2.5 pr-10 text-[13px] text-[#111827] outline-none placeholder:text-[#A5A5A5]"
        />
        <Calendar
          size={18}
          strokeWidth={2}
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
        />
        {/* Native picker overlaid on the calendar icon only — the rest of the
            field stays typeable. */}
        <input
          type="date"
          aria-label="Pick date of birth"
          onChange={onPick}
          className="absolute right-0 top-0 h-full w-11 cursor-pointer opacity-0"
        />
      </div>
    </div>
  );
}
